import { type ComponentProps } from 'react';

import {
  RadioGroup,
  type RadioGroupProps,
  type RadioOption,
} from '@storebrand-digital/pretzel';

import { isDefined, type DistributiveOmit } from '@bank/utils';

import type { Language } from '../language';

export interface YesAndNoRadioGroupProps extends DistributiveOmit<
  ComponentProps<typeof RadioGroup>,
  'options' | 'onChange' | 'onValueChange' | 'value' | 'direction' | 'alert'
> {
  language: Language;
  alert?: 'required' | RadioGroupProps['alert'];
  onValueChange: (value: boolean) => void;
  value?: boolean | null;
  order?: 'no-yes' | 'yes-no';
}

const texts = {
  no: {
    no: 'Nei',
    yes: 'Ja',
    validation: 'Vennligst velg ett alternativ',
  },
  en: {
    no: 'No',
    yes: 'Yes',
    validation: 'Please select one option',
  },
} satisfies Record<Language, { no: string; yes: string; validation: string }>;

export function YesAndNoRadioGroup({
  language,
  alert,
  value,
  onValueChange,
  order = 'no-yes',
  ...props
}: YesAndNoRadioGroupProps) {
  const text = texts[language];

  const options = [
    { label: text.no, value: 'false' },
    { label: text.yes, value: 'true' },
  ] satisfies RadioOption[];

  return (
    <RadioGroup
      direction="column"
      options={(order === 'yes-no' ? [...options].reverse() : options).map(
        (it) =>
          ({
            ...it,
            checked: isDefined(value) && value.toString() === it.value,
          }) satisfies RadioOption,
      )}
      alert={
        alert === 'required'
          ? {
              type: 'error',
              message: text.validation,
            }
          : alert
      }
      onValueChange={(newValue) => onValueChange(newValue === 'true')}
      isBorder
      fluid
      {...props}
    />
  );
}
